import { WellDetailsStates } from 'business/models/CustomModels';
import AlertBox from 'components/alerts/alertBox';
import { ButtonsListContainer } from 'components/Button';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import RMSvgIcon, {
  RMSvgIconProps,
} from 'components/customComponents/CommonComponents/RMSvgIcon';
import { useModalHandlers } from 'components/modal';
import { DropDetails } from 'components/well/constants';
import { OverlayTools } from 'components/well/drawingManager/constants';
import useRMLite from 'CustomHooks/useRMLite';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import React from 'react';
import { useRMDispatch } from 'redux/hooks';
import { useDropLocation, useSelectedDrawingTool } from 'redux/hooks/well';
import { setDrawingTool } from 'redux/reducers/wellReducer';

interface Props {
  wellDetails?: DropDetails | WellDetailsStates;
  expId?: number;
  isModifyAccess?: boolean;
  isDropImageAvailable?: boolean;
  imagePixelSize?: number;
  regionName?: string;
  enforceConditions?: boolean;
  hasOverview?: boolean;
  drawingsStatus?: EFetchingStatus;
}
const drawingToolActiveClass = 'bg-[#85baff]';
const drawingToolClass = 'w-[40px] h-[40px]';
const infoIconProps: RMSvgIconProps = {
  name: 'Info',
  fill: '#006AF5',
  width: '20px',
  height: '22px',
};

const getModalBody = () => (
  <>
    <AlertBox
      title="Switch to Overview region to use drop location."
      backgroundColor="#111111"
      iconProps={infoIconProps}
    />
    <p id='drawingcreationtools-modal-message'>{"Drop location isn't available in this region."}</p>
  </>
);

const DrawingCreationTools = (props: Props) => {
  const {
    isModifyAccess,
    imagePixelSize,
    isDropImageAvailable,
    regionName,
    enforceConditions = true,
    hasOverview,
    drawingsStatus,
  } = props;
  const selectedDrawingTool = useSelectedDrawingTool();
  const dispatch = useRMDispatch();
  const { showModal, hideModal } = useModalHandlers();
  const dropLocation = useDropLocation();
  const rmLite = useRMLite();

  const handleDropLocationClick = () => {
    if (hasOverview && regionName !== 'Overview') {
      showModal({
        body: getModalBody(),
        heading: 'Drop Location',
        okButtonText: 'OK',
        isOkOnly: true,
        onOk: hideModal,
      });
    } else {
      dispatch(setDrawingTool(OverlayTools.DROPLOCATION));
    }
  };

  const toolButtons: RMCustomButtonProps[] = [
    {
      Icon: <RMSvgIcon  id="drawcreationtools-pointer-icon" name="Pointer" height={30} width={30} />,
      titleTip: 'Pointer',
      color: '#0059CC',
      contentContainerClass: `${drawingToolClass} ${
        selectedDrawingTool === OverlayTools.POINTER ? drawingToolActiveClass : ''
      }`,
      onClick: () => {
        dispatch(setDrawingTool(OverlayTools.POINTER));
      },
      isDisabled: enforceConditions && !(isModifyAccess && isDropImageAvailable),
    },
    {
      Icon: <RMSvgIcon id="drawcreationtools-roi-icon" name="ROI" height={30} width={30} />,
      contentContainerClass: `${drawingToolClass} ${
        selectedDrawingTool === OverlayTools.ROI ? drawingToolActiveClass : ''
      }`,
      onClick: () => {
        dispatch(setDrawingTool(OverlayTools.ROI));
      },
      isDisabled:
        enforceConditions &&
        (!(isModifyAccess && isDropImageAvailable && drawingsStatus !== undefined) ||
          regionName === 'Overview' ||
          imagePixelSize === null ||
          regionName.toLowerCase().includes('region')),
      titleTip: 'ROI',
    },
    {
      Icon: <RMSvgIcon id="drawcreationtools-annote-icon" name="Annotate" height={30} width={30} />,
      contentContainerClass: `${drawingToolClass} ${
        selectedDrawingTool === OverlayTools.ANNOTATION ? drawingToolActiveClass : ''
      }`,
      onClick: () => {
        dispatch(setDrawingTool(OverlayTools.ANNOTATION));
      },
      isDisabled:
        (enforceConditions &&
          !(isModifyAccess && isDropImageAvailable && drawingsStatus !== undefined)) ||
        imagePixelSize === null,
      titleTip: 'Annotation',
    },
    {
      Icon: <RMSvgIcon id="drawcreationtools-droplocation-icon" name="DropLocation" height={30} width={30} />,
      contentContainerClass: `${drawingToolClass} ${
        selectedDrawingTool === OverlayTools.DROPLOCATION ? drawingToolActiveClass : ''
      }`,
      onClick: handleDropLocationClick,
      isDisabled:
        (enforceConditions &&
          !(isModifyAccess && isDropImageAvailable && drawingsStatus !== undefined)) ||
        imagePixelSize === null ||
        !!dropLocation?.length ||
        !hasOverview,
      titleTip: 'Drop Location',
    },
    {
      Icon: <RMSvgIcon id="drawcreationtools-scribble-icon" name="Scribble" height={30} width={30} />,
      contentContainerClass: `${drawingToolClass} ${
        selectedDrawingTool === OverlayTools.SCRIBBLE ? drawingToolActiveClass : ''
      }`,
      onClick: () => {
        dispatch(setDrawingTool(OverlayTools.SCRIBBLE));
      },
      isDisabled:
        (enforceConditions &&
          !(isModifyAccess && isDropImageAvailable && drawingsStatus !== undefined)) ||
        imagePixelSize === null,
      titleTip: 'Scribble',
    },
    {
      Icon: <RMSvgIcon id="drawcreationtools-ruler-icon" name="Ruler" height={30} width={30} />,
      contentContainerClass: `${drawingToolClass} ${
        selectedDrawingTool === OverlayTools.RULER ? drawingToolActiveClass : ''
      }`,
      onClick: () => {
        dispatch(setDrawingTool(OverlayTools.RULER));
      },
      isDisabled:
        (enforceConditions &&
          !(isModifyAccess && isDropImageAvailable && drawingsStatus !== undefined)) ||
        imagePixelSize === null,
      titleTip: 'Ruler',
    },
    ...(!rmLite
      ? [
          {
            Icon: <RMSvgIcon id="drawcreationtools-poi-icon" name="POI" height={30} width={30} />,
            contentContainerClass: `${drawingToolClass} ${
              selectedDrawingTool === OverlayTools.POI ? drawingToolActiveClass : ''
            }`,
            onClick: () => {
              dispatch(setDrawingTool(OverlayTools.POI));
            },
            isDisabled:
              rmLite ||
              (enforceConditions &&
                !(
                  isModifyAccess &&
                  isDropImageAvailable &&
                  drawingsStatus !== undefined
                )) ||
              imagePixelSize === null,
            titleTip: 'POI',
          },
        ]
      : []),
  ];
  return (
    <div id="drawingcreationtools-wrapper" className="flex flex-row w-full">
      <div
        id="drawingcreationtools-title"
        style={{
          paddingBlock: '2%',
          paddingInline: '1%',
        }}
      >
        Image Tools
      </div>
      <div
        id="drawingcreationtools-button-list"
        style={{
          paddingBlock: '2%',
          paddingInline: '5%',
        }}
      >
        <ButtonsListContainer
          containerStyleClass="align-items-center"
          buttonsList={toolButtons}
          marginBetweenItems={5}
        />
      </div>
    </div>
  );
};

export default DrawingCreationTools;
